import { P, usePermission } from "@crm/access/authorization/client";
import { ListPageShell, PageHeader } from "@crm/ui/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@crm/ui/components/ui/card";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { Option } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { BookOpen, CheckCircle, Clock, FileEdit } from "lucide-react";
import { useKnowledgeEntryStats } from "#app/knowledge/knowledge.queries";
import { KnowledgeNav } from "#components/knowledge";

export const Route = createFileRoute("/_protected/knowledge/")({
  head: () => ({}),
  component: KnowledgePage,
});

function KnowledgePage() {
  const navigate = useNavigate();
  const canReview = usePermission(P.KNOWLEDGE_ENTRY_REVIEW_ALL);
  const canClaim = usePermission(P.KNOWLEDGE_ENTRY_CLAIM_ALL);
  const canWriteAll = usePermission(P.KNOWLEDGE_ENTRY_WRITE_ALL);
  const canWriteAssigned = usePermission(P.KNOWLEDGE_ENTRY_WRITE_ASSIGNED);
  const canWrite = canWriteAll || canWriteAssigned;

  // 使用统计 API（性能优化：~100 bytes vs 20MB）
  const statsResult = useKnowledgeEntryStats();
  const stats = AsyncResult.value(statsResult).pipe(Option.getOrUndefined);

  const pendingReview = stats?.pendingReview ?? 0;
  const approved = stats?.approved ?? 0;
  const drafts = stats?.drafts ?? 0;
  const published = stats?.published ?? 0;

  if (!canReview && !canClaim && !canWrite) {
    return <Navigate to="/knowledge/articles" />;
  }

  return (
    <ListPageShell>
      <KnowledgeNav />
      <div className="mt-4">
        <PageHeader title="知识库管理" />
      </div>

      {/* 工作流卡片 - 可点击跳转 */}
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {canReview && (
          <Card
            className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md"
            onClick={() => navigate({ to: "/knowledge/review" })}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-medium text-sm">待审核</CardTitle>
              <Clock className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="font-bold text-2xl">{pendingReview}</div>
              <p className="mt-1 text-muted-foreground text-xs">AI 提取的待审核条目</p>
            </CardContent>
          </Card>
        )}

        {canClaim && (
          <Card
            className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md"
            onClick={() => navigate({ to: "/knowledge/pool" })}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-medium text-sm">认领池</CardTitle>
              <CheckCircle className="h-4 w-4 text-info" />
            </CardHeader>
            <CardContent>
              <div className="font-bold text-2xl">{approved}</div>
              <p className="mt-1 text-muted-foreground text-xs">已审核待认领的条目</p>
            </CardContent>
          </Card>
        )}

        {canWrite && (
          <Card
            className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md"
            onClick={() => navigate({ to: "/knowledge/drafts" })}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-medium text-sm">草稿</CardTitle>
              <FileEdit className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              <div className="font-bold text-2xl">{drafts}</div>
              <p className="mt-1 text-muted-foreground text-xs">编辑中的文章</p>
            </CardContent>
          </Card>
        )}

        <Card
          className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md"
          onClick={() => navigate({ to: "/knowledge/articles" })}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-medium text-sm">已发布</CardTitle>
            <BookOpen className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl">{published}</div>
            <p className="mt-1 text-muted-foreground text-xs">可供查阅的知识文档</p>
          </CardContent>
        </Card>
      </div>
    </ListPageShell>
  );
}
